import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { Customer } from '../models/Customer';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { GatewayCustomer } from '../models/GatewayCustomer';
import { GatewayContact } from '../models/GatewayContact';
import { UserMeResponse } from './UserService';

export interface UpdateCoinsModel {
  userName: string;
  coins: number;
  operation: 'add' | 'subtract' | 'add-refund';
}

@Injectable()
export default class CustomerService {
  baseUrl = `${environment.backend.baseUrl}${environment.backend.apiUrl}/`;
  constructor(private http: HttpClient) {}

  getAllCustomers() : Observable<GatewayCustomer[]> {
    return this.http.get<GatewayCustomer[]>(this.baseUrl + 'Gateway/customers');
  }

  getRegisteredCustomers() : Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseUrl + 'Customers');
  }

  getCustomerById(id: number): Observable<Customer> {
    return this.http.get<Customer>(this.baseUrl + 'Customers/' + id);
  }

  getCustomerByUserName(userName: string): Observable<Customer> {
    return this.http.get<Customer>(this.baseUrl + 'Customers/byUserName/' + userName);
  }

  /**
   * @deprecated Use UserService.getMe() instead. This method now uses /api/User/me internally.
   * The new endpoint returns a unified response with userType, user, customer, and roles.
   */
  getMe(): Observable<Customer> {
    return this.http.get<UserMeResponse>(this.baseUrl + 'User/me').pipe(
      map(response => {
        if (response.userType === 'customer' && response.customer) {
          return response.customer;
        }
        throw new Error('User is not a customer');
      })
    );
  }

  /**
   * @deprecated Use UserService.getMe() instead. This method now uses /api/User/me internally.
   * Raw response to inspect status codes (e.g., 201 on lazy bootstrap)
   */
  getMeRaw(): Observable<HttpResponse<Customer>> {
    return this.http.get<UserMeResponse>(this.baseUrl + 'User/me', { observe: 'response' }).pipe(
      map(response => {
        const customer = response.body?.userType === 'customer' && response.body?.customer
          ? response.body.customer
          : null;
        return new HttpResponse<Customer>({
          body: customer,
          headers: response.headers,
          status: response.status,
          statusText: response.statusText,
          url: response.url ?? undefined
        });
      })
    );
  }

  getCustomerByReflexCustomerNo(customerNo: string): Observable<Customer> {
    return this.http.get<Customer>(this.baseUrl + 'Customers/byReflexCustomerNo/' + customerNo);
  }

  getGatewayCustomerByReflexCustomerNo(customerNo: string): Observable<GatewayCustomer> {
    return this.http.get<GatewayCustomer>(this.baseUrl + 'Gateway/customers/' + customerNo);
  }

  updateCoins(model: UpdateCoinsModel): Observable<Customer> {
    return this.http.post<Customer>(
      this.baseUrl + 'Customers/updateCoins',
      model
    );
  }

  getContactDataByNameShorthand(shorthand: string): Observable<GatewayContact> {
    return this.http.get<GatewayContact>(this.baseUrl + 'Gateway/customers/contact/' + shorthand);
  }

  /**
   * @deprecated This endpoint has been moved to EmployeeService. Use EmployeeService.setCustomerEntraUserEnabledState() instead.
   * Will be removed in v2.0.
   */
  setEntraUserEnabledState(customerId: number, action: "enable" | "disable"): Observable<Customer[]> {
    return this.http.post<Customer[]>(this.baseUrl + 'Employee/customer/' + customerId + '/entra/' + action, { });
  }

  sendInvitation(email: string, message: string, subject?: string): Observable<any> {
    return this.http.post(
      this.baseUrl + 'Customers/sendInvitation',
      {
        email: email,
        message: message,
        subject: subject || 'Einladung zur NicLen Plattform'
      }
    );
  }

  sendInviteWithLink(customerNo: number, email: string, message: string): Observable<any> {
    return this.http.post(
      this.baseUrl + 'invites/send',
      {
        customerNo: customerNo,
        email: email,
        message: message
      }
    );
  }

  bootstrapInvite(state: string): Observable<any> {
    return this.http.post(
      this.baseUrl + 'Customers/bootstrap',
      {
        state: state
      }
    );
  }

  getPendingCustomers(): Observable<Customer[]> {
    return this.http.get<Customer[]>(
      this.baseUrl + 'Customers/pending'
    );
  }

  approveCustomer(customerId: number): Observable<any> {
    return this.http.post(
      this.baseUrl + `Customers/approve/${customerId}`,
      {}
    );
  }

  rejectCustomer(customerId: number): Observable<any> {
    return this.http.post(
      this.baseUrl + `Customers/reject/${customerId}`,
      {}
    );
  }

  approveBulk(customerIds: number[]): Observable<any> {
    return this.http.post(
      this.baseUrl + 'Customers/approve-bulk',
      customerIds
    );
  }

  rejectBulk(customerIds: number[]): Observable<any> {
    return this.http.post(
      this.baseUrl + 'Customers/reject-bulk',
      customerIds
    );
  }

  deactivateCustomer(customerId: number): Observable<any> {
    return this.http.post(
      this.baseUrl + `Customers/deactivate/${customerId}`,
      {}
    );
  }

  activateCustomer(customerId: number): Observable<any> {
    return this.http.post(
      this.baseUrl + `Customers/activate/${customerId}`,
      {}
    );
  }

  resendApprovalEmail(customerId: number): Observable<any> {
    return this.http.post(
      this.baseUrl + `Customers/resend-approval-email/${customerId}`,
      {}
    );
  }

  deleteCustomer(customerId: number): Observable<any> {
    return this.http.post(
      this.baseUrl + `Customers/delete/${customerId}`,
      {}
    );
  }

  registerWithInvite(registerData: { nonce: string; signature: string; firstName: string; lastName: string; email?: string }): Observable<any> {
    return this.http.post(
      this.baseUrl + 'Register/register-invite',
      registerData
    );
  }

  getCustomerEmployees(): Observable<Customer[]> {
    return this.http.get<Customer[]>(this.baseUrl + `Customers/employees`);
  }

  getCompanyNameByCustomerNos(customerNos: number[]): Observable<{ customerNo: number; companyShortName: string }[]> {
    return this.http.post<{ customerNo: number; companyShortName: string }[]>(this.baseUrl + 'Gateway/customers/companyNames', customerNos);
  }

  // Parent customer - child account management
  getMyChildCustomers(): Observable<any[]> {
    return this.http.get<any[]>(this.baseUrl + 'Customers/my-children');
  }

  activateChildCustomer(childId: number): Observable<any> {
    return this.http.post(this.baseUrl + `Customers/children/${childId}/activate`, {});
  }

  deactivateChildCustomer(childId: number): Observable<any> {
    return this.http.post(this.baseUrl + `Customers/children/${childId}/deactivate`, {});
  }
}
